import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DateTimeUtils } from '../../../shared/utils/date-time-utils';
import { NgIf } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';

export interface DateRangeSelection {
  start: Date;
  end: Date;
}

interface QuickOption {
  label: string;
  value: string;
  getValue: () => DateRangeSelection;
}

interface DateRangeOption {
  label: string;
  value: DateRangeType;
}

type DateRangeType = 'day' | 'week' | 'month' | 'year';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [DatePicker, Select, FormsModule, Button, NgIf, Tooltip],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.css',
})
export class DateRangePickerComponent implements OnInit {
  @ViewChild('datePicker') datePicker!: DatePicker;

  @Input() quickOptionsAvailable: boolean = true;
  @Input() range: Date[] | null = null;
  @Output() rangeChange = new EventEmitter<DateRangeSelection>();
  @Output() previousWeek = new EventEmitter<void>();
  @Output() nextWeek = new EventEmitter<void>();

  minDate: Date | null = null;
  maxDate: Date | null = null;

  // @Output() navigate = new EventEmitter<{
  //   direction: -1 | 1;
  //   range: DateRangeType;
  // }>();
  // @Output() nextWeek = new EventEmitter<string>();

  selectedQuickOption: QuickOption | null = null;
  selectedDateRangeOption: DateRangeOption | null = null;

  readonly dateRangeOptions: DateRangeOption[] = [
    {
      label: 'Dzień',
      value: 'day',
    },
    {
      label: 'Tydzień',
      value: 'week',
    },
    {
      label: 'Miesiąc',
      value: 'month',
    },
    {
      label: 'Rok',
      value: 'year',
    },
  ];

  readonly quickOptions: QuickOption[] = [
    {
      label: 'Dzisiaj',
      value: 'today',
      getValue: () => this.getToday(),
    },
    {
      label: 'Wczoraj',
      value: 'yesterday',
      getValue: () => this.getYesterday(),
    },
    {
      label: 'Ten tydzień',
      value: 'this-week',
      getValue: () => this.getThisWeek(),
    },
    {
      label: 'Poprzedni tydzień',
      value: 'last-week',
      getValue: () => this.getLastWeek(),
    },
    {
      label: 'Ten miesiąc',
      value: 'this-month',
      getValue: () => this.getThisMonth(),
    },
    {
      label: 'Poprzedni miesiąc',
      value: 'last-month',
      getValue: () => this.getLastMonth(),
    },
    {
      label: 'Ten rok',
      value: 'this-year',
      getValue: () => this.getThisYear(),
    },
    {
      label: 'Poprzedni rok',
      value: 'last-year',
      getValue: () => this.getLastYear(),
    },
  ];

  ngOnInit(): void {
    // Nie emituję eventu tutaj - logika URL jest obsługiwana przez komponent nadrzędny
    // Ustawiam tylko selectedQuickOption jeśli range odpowiada "Ten tydzień"
    if (this.range && this.range.length === 2) {
      const thisWeek = this.getThisWeek();
      const rangeStart = DateTimeUtils.localToUTC(this.range[0]);
      const rangeEnd = DateTimeUtils.localToUTC(this.range[1]);

      // czy zakres to "Ten tydzień"
      if (
        rangeStart.getTime() === thisWeek.start.getTime() &&
        rangeEnd.getTime() === thisWeek.end.getTime()
      ) {
        this.selectedQuickOption =
          this.quickOptions.find((opt) => opt.value === 'this-week') || null;
      }
    }
  }

  onDateRangeSelect(): void {
    const dateRange = this.range;
    if (!dateRange) return;

    const dates = Array.isArray(dateRange) ? dateRange : [dateRange];

    if (dates.length === 1 || !dates[1]) {
      const startDate = dates[0];
      if (!startDate) return;

      const max = new Date(startDate);
      max.setDate(max.getDate() + 365);

      this.minDate = startDate;
      this.maxDate = max;

      return;
    }

    const startDate = dates[0];
    const endDate = dates[1];

    if (!startDate || !endDate) return;

    if (DateTimeUtils.daysBetween(startDate, endDate) > 365) {
      return;
    }

    const utcStartDate = DateTimeUtils.localToUTC(startDate);
    const utcEndDate = DateTimeUtils.localToUTCEndDay(endDate);

    this.selectedQuickOption = null;

    this.rangeChange.emit({
      start: utcStartDate,
      end: utcEndDate,
    });

    this.minDate = null;
    this.maxDate = null;

    this.closeDatePicker();
  }

  onQuickOptionChange(): void {
    if (!this.selectedQuickOption) return;

    const range = this.selectedQuickOption.getValue();
    this.rangeChange.emit(range);
  }

  closeDatePicker(): void {
    if (this.datePicker) {
      this.datePicker.overlayVisible = false;
      this.datePicker.hideOverlay();
    }
  }

  onPreviousWeek(): void {
    this.selectedQuickOption = null;
    this.previousWeek.emit();
  }

  onNextWeek(): void {
    this.selectedQuickOption = null;
    this.nextWeek.emit();
  }

  // onPrevClick() {
  //   this.navigate.emit({ direction: -1, range: this.selectedDateRangeOption!.value });
  // }
  //
  // onNextClick() {
  //   this.navigate.emit({ direction: 1, range: this.selectedDateRangeOption!.value });
  // }

  private getToday(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const start = new Date(
      Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), utcNow.getUTCDate(), 0, 0, 0),
    );
    const end = new Date(
      Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), utcNow.getUTCDate(), 23, 59, 59),
    );
    return { start, end };
  }

  private getYesterday(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const yesterday = new Date(utcNow);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    const start = new Date(
      Date.UTC(
        yesterday.getUTCFullYear(),
        yesterday.getUTCMonth(),
        yesterday.getUTCDate(),
        0,
        0,
        0,
      ),
    );
    const end = new Date(
      Date.UTC(
        yesterday.getUTCFullYear(),
        yesterday.getUTCMonth(),
        yesterday.getUTCDate(),
        23,
        59,
        59,
      ),
    );
    return { start, end };
  }

  private getThisWeek(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    return DateTimeUtils.getWeekUTCFromDate(utcNow);
  }

  private getLastWeek(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const lastWeek = new Date(utcNow);
    lastWeek.setUTCDate(lastWeek.getUTCDate() - 7);
    return DateTimeUtils.getWeekUTCFromDate(lastWeek);
  }

  private getThisMonth(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1, 0, 0, 0));
    const end = new Date(
      Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59),
    );
    return { start, end };
  }

  private getLastMonth(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1, 0, 0, 0));
    const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59));
    return { start, end };
  }

  private getThisYear(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const start = new Date(Date.UTC(utcNow.getUTCFullYear(), 0, 1, 0, 0, 0));
    const end = new Date(Date.UTC(utcNow.getUTCFullYear(), 11, 31, 23, 59, 59));
    return { start, end };
  }

  private getLastYear(): DateRangeSelection {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    const start = new Date(Date.UTC(utcNow.getUTCFullYear() - 1, 0, 1, 0, 0, 0));
    const end = new Date(Date.UTC(utcNow.getUTCFullYear() - 1, 11, 31, 23, 59, 59));
    return { start, end };
  }
}
